<template>
  <section class="top-courses-section">
    <div class="section-header">
      <h2 class="section-title">Top Performing Courses</h2>
      <div class="time-filter">
        <select v-model="timeframe" class="filter-select">
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
      </div>
    </div>
    
    <div class="courses-grid">
      <div v-for="course in topCourses" :key="course.id" class="course-card">
        <div class="course-image">
          <img :src="course.image" :alt="course.title" />
          <div v-if="course.badge" class="course-badge" :class="course.badge.type">
            {{ course.badge.text }}
          </div>
        </div>
        <div class="course-content">
          <h3 class="course-title">{{ course.title }}</h3>
          <p class="course-instructor">{{ course.instructor }}</p>
          <div class="course-stats">
            <div class="stat">
              <span class="stat-value">{{ course.enrollments }}</span>
              <span class="stat-label">Enrollments</span>
            </div>
            <div class="stat">
              <span class="stat-value">{{ course.completion }}%</span>
              <span class="stat-label">Completion</span>
            </div>
            <div class="stat">
              <span class="stat-value">{{ course.rating.toFixed(1) }}</span>
              <span class="stat-label">Rating</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'TopCoursesSection',
  props: {
    coursesData: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const timeframe = ref('month');
    
    const topCourses = computed(() => {
      // If real data is provided, use it
      if (props.coursesData && props.coursesData.length > 0) {
        return props.coursesData;
      }
      
      // Otherwise use dummy data
      return [
        {
          id: 1,
          title: 'Machine Learning Fundamentals',
          instructor: 'Dr. Sarah Johnson',
          image: 'https://via.placeholder.com/300x180?text=ML+Course',
          enrollments: 1245,
          completion: 78,
          rating: 4.8,
          badge: { type: 'trending', text: 'Trending' }
        },
        {
          id: 2,
          title: 'Web Development Bootcamp',
          instructor: 'Michael Chen',
          image: 'https://via.placeholder.com/300x180?text=Web+Dev',
          enrollments: 987,
          completion: 65,
          rating: 4.6,
          badge: { type: 'popular', text: 'Popular' }
        },
        {
          id: 3,
          title: 'Python for Data Science',
          instructor: 'Alex Rivera',
          image: 'https://via.placeholder.com/300x180?text=Python',
          enrollments: 1087,
          completion: 72,
          rating: 4.7,
          badge: { type: 'new', text: 'New' }
        },
        {
          id: 4,
          title: 'UX/UI Design Principles',
          instructor: 'Emma Watson',
          image: 'https://via.placeholder.com/300x180?text=UX+Design',
          enrollments: 856,
          completion: 81,
          rating: 4.9,
          badge: null
        }
      ];
    });
    
    return {
      timeframe,
      topCourses
    };
  }
};
</script>

<style scoped>
.top-courses-section {
  @apply bg-white rounded-lg shadow p-6;
}

.section-header {
  @apply flex justify-between items-center mb-6;
}

.section-title {
  @apply text-xl font-semibold text-gray-800;
}

.time-filter {
  @apply relative;
}

.filter-select {
  @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
  focus:ring-blue-500 focus:border-blue-500 block p-2.5;
}

.courses-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6;
}

.course-card {
  @apply bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden 
  transition-all duration-300 hover:shadow-md;
}

.course-image {
  @apply relative h-40 w-full;
}

.course-image img {
  @apply h-full w-full object-cover;
}

.course-badge {
  @apply absolute top-3 right-3 py-1 px-3 text-xs font-medium rounded-full text-white;
}

.course-badge.trending {
  @apply bg-orange-500;
}

.course-badge.popular {
  @apply bg-purple-500;
}

.course-badge.new {
  @apply bg-green-500;
}

.course-content {
  @apply p-4;
}

.course-title {
  @apply text-base font-medium text-gray-800 mb-1 line-clamp-1;
}

.course-instructor {
  @apply text-sm text-gray-600 mb-3;
}

.course-stats {
  @apply flex justify-between items-center;
}

.stat {
  @apply flex flex-col items-center;
}

.stat-value {
  @apply text-sm font-semibold text-gray-800;
}

.stat-label {
  @apply text-xs text-gray-500;
}

@media (prefers-color-scheme: dark) {
  .top-courses-section {
    @apply bg-gray-800;
  }
  
  .section-title {
    @apply text-gray-100;
  }
  
  .filter-select {
    @apply bg-gray-700 border-gray-600 text-gray-100;
  }
  
  .course-card {
    @apply bg-gray-800 border-gray-700;
  }
  
  .course-title {
    @apply text-gray-100;
  }
  
  .course-instructor {
    @apply text-gray-400;
  }
  
  .stat-value {
    @apply text-gray-100;
  }
  
  .stat-label {
    @apply text-gray-400;
  }
}
</style> 